import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { AddFile } from 'assets/svg';
import TileButton from 'components/atoms/TileButton';
import { sendNotification } from 'utils/helperMethods';
import ScenariosContainer from './ScenariosContainer';
import ScenarioInformation from './ScenarioInformation';
import { useFetchScenarioForComparison } from 'hooks/priceSimulator/useFetchScenarioForComparison';
import AddScenarioModal from '../Modals/AddScenarioModal';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PSR_COMP_SELECTE_PLANS_BTN_CLICKED,
    PSR_COMP_VS_ACTUALS_VS_GOALS_TILE_CHANGED,
} from 'utils/mixpanelEventNames';

type ComparisonType = {
    selectedRowKeys: Array<number>;
    setSelectedRowKeys: Function;
    allScenariosInfo: Array<{ name: string; scenarioId: number; dataSourceType: string }>;
};

export const comparisonTileData = [
    { key: 'OutputVsActuals', label: 'vs Actuals' },
    { key: 'OutputVsGoals', label: 'vs Goals' },
];

const Comparison = ({ selectedRowKeys: propSelectedRowKeys, allScenariosInfo }: ComparisonType) => {
    const [comparisonTile, setComparisionTile] = useState(comparisonTileData[0]);
    const [selectedCard, setSelectedCard] = useState(0);
    const [scenarioCardsDetails, setScenarioCardsDetails] = useState<any>({});
    const [processingId, setProcessingId] = useState(0);
    const [isAddScenarioModalVisible, setAddScenarioModalVisible] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
    const [orderedSelectedInputKpiOpitions, setOrderedSelectedInputKpiOpitions] = useState<any[]>([]);

    // fetch single scenario GQL
    const {
        loading: fetchScenarioLoading,
        data: fetchScenarioData,
        error: fetchScenarioError,
        fetchScenario,
    } = useFetchScenarioForComparison();

    useEffect(() => {
        if (!propSelectedRowKeys.length) return;
        sendNotification('success', `${propSelectedRowKeys.length} Plans added for comparison`);
        const newScenarioCardDetails: any = {};
        propSelectedRowKeys.forEach((scenarioId: number) => {
            newScenarioCardDetails[scenarioId] = {
                loading: true,
                data: null,
                error: '',
                scenarioId: scenarioId,
                scenarioName: allScenariosInfo.find((pair: any) => pair.scenarioId === scenarioId)?.name,
            };
        });
        setScenarioCardsDetails(newScenarioCardDetails);
        setSelectedRowKeys([...propSelectedRowKeys]);
    }, [propSelectedRowKeys]);

    useEffect(() => {
        if (Object.keys(scenarioCardsDetails || {}).length) {
            processFetchingPayload();
        }
    }, [scenarioCardsDetails]);

    useEffect(() => {
        if (fetchScenarioData) {
            const newScenarioCardDetails: any = { ...scenarioCardsDetails };
            scenarioCardsDetails[processingId].data = fetchScenarioData;
            scenarioCardsDetails[processingId].loading = false;

            setScenarioCardsDetails(newScenarioCardDetails);
        }
    }, [fetchScenarioData]);

    useEffect(() => {
        if (fetchScenarioError === 'failed') {
            const newScenarioCardDetails: any = { ...scenarioCardsDetails };
            scenarioCardsDetails[processingId].error = 'Failed';
            scenarioCardsDetails[processingId].loading = false;
            setScenarioCardsDetails(newScenarioCardDetails);
        }
    }, [fetchScenarioError]);

    const onRemoveCardHandler = (scenarioId: number) => {
        const newIds = selectedRowKeys.filter((num: number) => num !== scenarioId);
        setSelectedRowKeys(newIds);
    };

    const closeLinkingModal = () => {
        setAddScenarioModalVisible(false);
    };

    const onApplyChange = (newSelectedKeys: Array<number>) => {
        const newSelectedRowKeys: number[] = [];
        const removedKeys: Array<number> = [];

        if (!newSelectedKeys.includes(selectedCard)) {
            setSelectedCard(0);
        }

        selectedRowKeys.forEach((num: number) => {
            if (newSelectedKeys.includes(num)) {
                newSelectedRowKeys.push(num);
            } else {
                removedKeys.push(num);
            }
        });

        const newScenarioCardsDetails = { ...scenarioCardsDetails };

        // removing unchecked scenario details
        removedKeys.forEach((key) => {
            delete newScenarioCardsDetails[key];
        });

        // adding new scenario details with loading  set to true
        newSelectedKeys.forEach((key: number) => {
            if (!newSelectedRowKeys.includes(key)) {
                newScenarioCardsDetails[key] = {
                    loading: true,
                    data: null,
                    error: '',
                    scenarioId: key,
                    scenarioName: allScenariosInfo.find((pair: any) => pair.scenarioId === key)?.name,
                };
            }
        });
        setScenarioCardsDetails(newScenarioCardsDetails);
        setSelectedRowKeys(newSelectedKeys);
    };

    function processFetchingPayload() {
        if (scenarioCardsDetails) {
            const pendingFetchId = selectedRowKeys.find((item: number) => scenarioCardsDetails[item].loading);
            if (pendingFetchId) {
                setProcessingId(Number(pendingFetchId));
                fetchScenario({
                    variables: {
                        scenarioId: Number(pendingFetchId),
                    },
                });
            }
        }
    }

    const completeDataForComparison = useMemo(() => {
        const finalExcelData = [
            [comparisonTile.key === comparisonTileData[0].key ? 'Simulated Vs Actuals' : 'Simulated Vs Goals'],
        ];
        const header = ['KPI', ''];
        const subHeader = ['', ''];

        selectedRowKeys.forEach((key: number) => {
            header.push(scenarioCardsDetails[key].scenarioName);
            header.push('');
            header.push('');
            header.push('');

            subHeader.push(comparisonTile.key === comparisonTileData[0].key ? 'Actual' : 'Goal');
            subHeader.push('Abs Chg');
            subHeader.push('% Chg');
            subHeader.push('');
        });

        // adding headers
        finalExcelData.push(header);
        finalExcelData.push(subHeader);

        orderedSelectedInputKpiOpitions.forEach((kpi: any) => {
            const eachKpiRowData = [kpi.label, ''];

            selectedRowKeys.forEach((key: any) => {
                const target = scenarioCardsDetails[key]?.data?.scenarioComparisonData?.[comparisonTile.key]?.find(
                    (k: any) => k.KPI === kpi.label,
                );
                // adding individual rows data
                if (target) {
                    eachKpiRowData.push(target.value);
                    eachKpiRowData.push(target.absChg);
                    eachKpiRowData.push(target.percentageChg);
                    eachKpiRowData.push('');
                }
            });
            finalExcelData.push(eachKpiRowData);
        });

        return finalExcelData;
    }, [comparisonTile.key, selectedRowKeys, scenarioCardsDetails, orderedSelectedInputKpiOpitions]);

    return (
        <div id="price-sim-comparison">
            <div className="my-2 font-sm text-promoColour">
                In this page you can compare different scenarios plans.
                {selectedRowKeys.length > 0 && (
                    <span>
                        Data source
                        {` ${allScenariosInfo.find((s: any) => s.scenarioId === selectedRowKeys[0])?.dataSourceType} `}
                        is selected for comparison.
                    </span>
                )}
            </div>
            <div className="flex justify-between items-center">
                <button
                    className="mb-2 rounded cursor-pointer bg-purpleFont hover:text-white text-white font-semibold py-2 px-4 flex items-center"
                    onClick={() => {
                        setAddScenarioModalVisible(true);
                        trackEventMixpanel(PSR_COMP_SELECTE_PLANS_BTN_CLICKED, {});
                    }}
                    id="price-sim-comparison-select-plans-btn"
                >
                    <AddFile /> <span className="ml-2">Select Plans</span>
                </button>
                <div className="flex justify-center items-center gap-2">
                    <div className="flex justify-center items-center">
                        <span className="font-medium">Simulated</span>
                        <TileButton
                            disabled={false}
                            data={comparisonTileData}
                            activeKey={comparisonTile.key}
                            onClick={(item: any) => {
                                setComparisionTile(item);
                                trackEventMixpanel(PSR_COMP_VS_ACTUALS_VS_GOALS_TILE_CHANGED, {
                                    label: item.label,
                                });
                            }}
                            style={{ marginLeft: 12 }}
                        />
                    </div>
                    <DownloadExcel
                        title={'Comparison Data'}
                        excelData={[]}
                        headers={[]}
                        excludeFilters={true}
                        completeDataWithHeaders={completeDataForComparison}
                    />
                </div>
            </div>
            <ScenariosContainer
                scenarioCardsDetails={scenarioCardsDetails}
                selectedRowKeys={selectedRowKeys}
                selectedCard={selectedCard}
                setSelectedCard={setSelectedCard}
                onRemoveCardHandler={onRemoveCardHandler}
                comparisonTile={comparisonTile}
                orderedSelectedInputKpiOpitions={orderedSelectedInputKpiOpitions}
                setOrderedSelectedInputKpiOpitions={setOrderedSelectedInputKpiOpitions}
            />
            {selectedCard !== 0 ? (
                <ScenarioInformation cardDetails={scenarioCardsDetails[selectedCard]} />
            ) : (
                <div className="flex justify-center items-center p-36 border rounded mt-4 text-promoColour border-borderLine">
                    <span>Please click on any scenario to see more information.</span>
                </div>
            )}

            <AddScenarioModal
                visible={isAddScenarioModalVisible}
                closeModal={closeLinkingModal}
                allScenariosInfo={allScenariosInfo}
                selectedRowKeys={selectedRowKeys}
                onApplyChange={onApplyChange}
            />
        </div>
    );
};

export default Comparison;
